// @ts-nocheck — TODO: Add proper types (Phase 7)
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { mrnToShortCode } from '../utils/mrnUtils';
import useDebounce from '../hooks/useDebounce';
import { logPHIRead } from '../services/core/phiAccessLogger';
import { formatClinicalDate } from '@/utils/clinicalDate';

interface SearchPatient {
    id: string;
    firstName: string;
    lastName: string;
    dob: string;
    phone?: string;
    [key: string]: unknown;
}

export interface GlobalPatientSearchProps {
    /** Shorter field for the 48 px app bar; fills its container width. */
    compact?: boolean;
}

const GlobalPatientSearch = ({ compact = false }: GlobalPatientSearchProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<SearchPatient[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const debouncedSearch = useDebounce(searchTerm, 300);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) {
                // Command+K or Ctrl+K to open
                if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                    e.preventDefault();
                    setIsOpen(true);
                    setTimeout(() => inputRef.current?.focus(), 50);
                }
                return;
            }

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedIndex >= 0 && results[selectedIndex]) {
                        handleSelect(results[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    setIsOpen(false);
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, results, selectedIndex]);

    // Search effect
    useEffect(() => {
        const searchPatients = async () => {
            if (!debouncedSearch || debouncedSearch.length < 2) {
                setResults([]);
                return;
            }

            setLoading(true);
            try {
                const response = await API.get('?limit=100', { useCache: false });
                const allPatients = response.data?.patients || response.data || [];

                // HIPAA: Log bulk patient search access
                logPHIRead('patients', 'search').catch((e) => console.warn('[phi-audit] access-log write failed:', e?.message));

                const searchLower = debouncedSearch.toLowerCase();
                const filtered = allPatients.filter((p: SearchPatient) => {
                    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
                    const mrn = (p.id || '').toLowerCase();
                    const phone = (p.phone || '').replace(/\D/g, '');

                    return fullName.includes(searchLower) ||
                        mrn.includes(searchLower) ||
                        phone.includes(searchLower);
                }).slice(0, 10);

                setResults(filtered);
                setSelectedIndex(0);
            } catch (error: unknown) {
                // Search error — logged via API interceptor
            } finally {
                setLoading(false);
            }
        };

        searchPatients();
    }, [debouncedSearch]);

    const handleSelect = (patient: SearchPatient) => {
        navigate(`/patients/${patient.id}`);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative" ref={wrapperRef}>
            {/* Search Trigger / Input */}
            <div className={`relative transition-all duration-200 ${compact ? 'w-full' : isOpen ? 'w-64 md:w-80' : 'w-48 md:w-64'}`}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className={`w-4 h-4 text-slate-500 dark:text-slate-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    className={`
            w-full pl-10 pr-12 ${compact ? 'h-8 py-0' : 'py-2'} text-sm rounded-lg border outline-none transition-all
            border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100
          `}
                    placeholder={compact ? 'Find patient' : 'Search patients...'}
                    aria-label="Find patient"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSearchTerm(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className={`text-xs px-1.5 py-0.5 rounded-md border border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500`}>
                        ⌘K
                    </span>
                </div>
            </div>

            {/* Results Dropdown */}
            {isOpen && (searchTerm.length >= 2 || results.length > 0) && (
                <div className={`
          absolute top-full ${compact ? 'right-0' : 'left-0'} mt-2 w-full md:w-96 rounded-xl shadow-lg border overflow-hidden z-50
          bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-700
        `}>
                    {loading ? (
                        <div className="p-4 text-center">
                            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        </div>
                    ) : results.length > 0 ? (
                        <div className="py-2 max-h-[60vh] overflow-y-auto">
                            <div className="px-3 py-1 text-[13px] font-semibold text-slate-600 dark:text-slate-300">
                                Patients
                            </div>
                            {results.map((patient, index) => (
                                <button
                                    key={patient.id}
                                    onClick={() => handleSelect(patient)}
                                    className={`
                    w-full px-4 py-3 flex items-center gap-3 text-left transition-colors
                    ${index === selectedIndex
                                            ? ('bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-100')
                                            : ('hover:bg-slate-50 text-slate-700 dark:hover:bg-slate-800 dark:text-slate-200')}
                  `}
                                >
                                    <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold
                    ${index === selectedIndex
                                            ? ('bg-primary-100 text-primary-700 dark:bg-primary-800 dark:text-primary-100')
                                            : ('bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300')}
                  `}>
                                        {patient.firstName[0]}{patient.lastName[0]}
                                    </div>
                                    <div>
                                        <div className="font-medium">{patient.firstName} {patient.lastName}</div>
                                        <div className={`text-xs ${index === selectedIndex ? ('text-primary-600 dark:text-primary-200') : ('text-slate-500 dark:text-slate-400')}`}>
                                            MRN: {mrnToShortCode(patient.id)} • DOB: {formatClinicalDate(patient.dob, { month: 'numeric', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : searchTerm.length >= 2 ? (
                        <div className="p-8 text-center">
                            <p className="text-slate-500 dark:text-slate-400">No patients found matching &quot;{searchTerm}&quot;</p>
                        </div>
                    ) : null}

                    {/* Footer */}
                    <div className={`px-4 py-2 border-t text-xs flex justify-between bg-slate-50 border-slate-200 text-slate-500 dark:bg-slate-800/50 dark:border-slate-700 dark:text-slate-400`}>
                        <span>Select <kbd className="font-sans">↵</kbd></span>
                        <span>Navigate <kbd className="font-sans">↑↓</kbd></span>
                        <span>Close <kbd className="font-sans">esc</kbd></span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalPatientSearch;
